'use client'

import { useEffect } from 'react'

import Button from '@/components/ui/Button'
import { useConfigContext } from '@/context/ConfigProvider'

export default function Error({ error, reset }: { error: Error & { digest?: string }; reset: () => void }) {
  const { locale } = useConfigContext()
  const isDev = process.env.NODE_ENV === 'development'

  useEffect(() => {
    // Log error to console or error reporting service
    console.error('Error boundary caught:', error)
  }, [error])

  const text = (lang: string) => {
    if (lang === 'vi') {
      return {
        errorCode: '500',
        title: 'Lỗi Máy Chủ',
        subtitle: 'Đã có lỗi xảy ra',
        content: 'Xin lỗi vì sự bất tiện này. Đội ngũ kỹ thuật đã được thông báo và sẽ khắc phục sớm nhất.',
        btn: 'Về trang chủ',
        retry: 'Thử lại',
        errorDetails: 'Chi tiết lỗi',
      }
    } else {
      return {
        errorCode: '500',
        title: 'Server Error',
        subtitle: 'An error has occurred',
        content: 'Sorry for the inconvenience. Our technical team has been notified and will fix it as soon as possible.',
        btn: 'Back to home',
        retry: 'Try again',
        errorDetails: 'Error Details',
      }
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center px-4 py-8">
      <div className="max-w-2xl w-full">
        <div className="text-center mb-8">
          {/* Error Code */}
          <div className="mb-4">
            <span className="text-8xl md:text-9xl font-bold text-red-500 opacity-20">{text(locale).errorCode}</span>
          </div>

          {/* Error Icon/Image */}
          {/* <div className="mb-6">
            <Image 
              src="/assets/images/404.jpg" 
              alt="error" 
              width={400} 
              height={400} 
              className="w-64 md:w-80 max-w-md grayscale mx-auto opacity-60" 
              unoptimized 
            />
          </div> */}

          {/* Error Message */}
          <div className="space-y-3 mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900">{text(locale).title}</h1>
            <p className="text-xl text-gray-700">{text(locale).subtitle}</p>
            <p className="text-base text-gray-600 max-w-lg mx-auto">{text(locale).content}</p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center mb-8">
            <Button primary size="md" onClick={() => reset()} className="min-w-[140px]">
              {text(locale).retry}
            </Button>
            <Button primary size="md" href="/" className="min-w-[140px]">
              {text(locale).btn}
            </Button>
          </div>

          {/* Error Details for Development */}
          {isDev && error && (
            <div className="mt-8 p-6 bg-red-50 border border-red-200 rounded-lg text-left">
              <h2 className="text-lg font-semibold text-red-800 mb-3">{text(locale).errorDetails}</h2>
              <div className="space-y-2">
                <p className="text-sm font-mono text-red-700 break-all">
                  <strong>Message:</strong> {error.message}
                </p>
                {error.digest && (
                  <p className="text-sm font-mono text-red-600">
                    <strong>Digest:</strong> {error.digest}
                  </p>
                )}
                {error.stack && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-sm font-semibold text-red-800 hover:text-red-900">Stack Trace</summary>
                    <pre className="mt-2 text-xs bg-red-100 p-3 rounded overflow-x-auto text-red-900">{error.stack}</pre>
                  </details>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
